/* ============================================================================
   DOCUMENTS OF RECORD — every formal event between the studio and the client
   (an engagement issued or signed, drawings approved, the brief accepted) is
   emailed to both parties the moment it happens, and the outcome of that send
   is kept on the project. The badge says whether the email went; the button
   sends it again when it didn't.
   ========================================================================== */
import { useState } from "react";
import { actions } from "../lib/store";
import { Icon } from "./Icons";
import { fmtDate } from "../lib/format";
import { RECORD_LABEL } from "../lib/types";
import type { Project, SentRecord, RecordKind, RecordRecipient } from "../lib/types";

export function latestRecord(p: Project, kind: RecordKind, refId?: string): SentRecord | undefined {
  return (p.records ?? []).find((r) => r.kind === kind && (refId ? r.refId === refId : true));
}

export function recordState(r?: SentRecord): { tone: "ok" | "warn" | "danger" | "info" | ""; label: string; sending: boolean } {
  if (!r) return { tone: "", label: "Not emailed", sending: false };
  const rs = r.recipients;
  if (rs.some((x) => x.result === "pending")) return { tone: "info", label: "Sending…", sending: true };
  if (rs.length && rs.every((x) => x.result === "delivered")) return { tone: "ok", label: "Emailed to both parties", sending: false };
  if (rs.some((x) => x.result === "failed")) {
    const bad = rs.filter((x) => x.result === "failed").map((x) => x.party);
    return { tone: "danger", label: `Email failed — ${bad.join(" & ")}`, sending: false };
  }
  if (rs.some((x) => x.result === "simulated")) return { tone: "warn", label: "Email not configured", sending: false };
  return { tone: "", label: "Not emailed", sending: false };
}

function recipientTitle(x: RecordRecipient): string {
  return `${x.party === "client" ? "Client" : "Studio"}: ${x.to || "no address on file"} — ${x.result}${x.error ? ` (${x.error})` : ""}${x.on ? ` · ${fmtDate(x.on)}` : ""}`;
}

/** The email status of one record, with the manual trigger when it's needed. */
export function RecordBadge({ p, kind, refId, title, by, compact }: { p: Project; kind: RecordKind; refId?: string; title: string; by: string; compact?: boolean }) {
  const r = latestRecord(p, kind, refId);
  const st = recordState(r);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      if (r) await actions.sendRecord(p.id, r.id);
      else { const id = actions.addRecord(p.id, { kind, refId, title, by }); await actions.sendRecord(p.id, id); }
    } finally { setBusy(false); }
  };
  const when = r?.recipients.find((x) => x.on)?.on;
  return (
    <span className="row wrap" style={{ gap: 6, alignItems: "center" }}>
      <span className={"badge " + st.tone} style={{ fontSize: 9.5 }} title={r ? r.recipients.map(recipientTitle).join("\n") : "No email has gone out for this record yet"}>
        <Icon.send width={11} />{st.label}{st.tone === "ok" && when ? ` · ${fmtDate(when)}` : ""}
      </span>
      {(st.tone !== "ok" || !compact) && (
        <button className="btn ghost sm" style={{ fontSize: 10.5, padding: "3px 9px" }} disabled={busy || st.sending} onClick={() => void send()}>
          <Icon.send width={11} />{busy || st.sending ? "Sending…" : st.tone === "ok" ? "Send again" : "Send now"}
        </button>
      )}
    </span>
  );
}

/** The register of everything sent — newest first — with the per-party outcome. */
export function RecordsPanel({ p, kinds, title = "Documents of record", intro }: { p: Project; kinds?: RecordKind[]; title?: string; intro?: string }) {
  const list = (p.records ?? []).filter((r) => !kinds || kinds.includes(r.kind));
  const [busyId, setBusyId] = useState<string | null>(null);
  const resend = async (id: string) => { setBusyId(id); try { await actions.sendRecord(p.id, id); } finally { setBusyId(null); } };
  return (
    <div className="card reveal" style={{ overflow: "hidden" }}>
      <div className="row between wrap" style={{ padding: "14px 18px", borderBottom: "1px solid var(--line)", gap: 8 }}>
        <div className="row" style={{ gap: 8 }}><Icon.send width={15} style={{ color: "var(--accent-ink)" }} /><span className="kicker">{title}</span></div>
        <span className="dim" style={{ fontSize: 12 }}>{list.length ? `${list.filter((r) => recordState(r).tone === "ok").length} of ${list.length} emailed to both parties` : "nothing yet"}</span>
      </div>
      {intro && <p className="dim" style={{ fontSize: 12.5, margin: 0, padding: "10px 18px 0", lineHeight: 1.55 }}>{intro}</p>}
      {list.length === 0 && <div className="dim" style={{ padding: "16px 18px", fontSize: 13 }}>Formal events — the engagement, drawing approvals, the brief — are emailed to both parties as they happen and listed here with the outcome.</div>}
      {list.map((r, i) => {
        const st = recordState(r);
        return (
          <div key={r.id} style={{ padding: "12px 18px", borderTop: i ? "1px solid var(--line)" : "none" }}>
            <div className="row between wrap" style={{ gap: 10 }}>
              <span className="row" style={{ gap: 9, minWidth: 0, alignItems: "baseline" }}>
                <span className="badge accent" style={{ fontSize: 9.5 }}>{RECORD_LABEL[r.kind]}</span>
                <span style={{ fontSize: 13.5, fontWeight: 600 }}>{r.title}</span>
              </span>
              <span className={"badge " + st.tone} style={{ fontSize: 9.5 }} title={r.recipients.map(recipientTitle).join("\n")}><Icon.send width={11} />{st.label}</span>
            </div>
            <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>{r.by} · {fmtDate(r.on)}</div>
            <div className="row wrap" style={{ gap: 6, marginTop: 8, alignItems: "center" }}>
              {r.recipients.map((x) => (
                <span key={x.party} className={"badge " + (x.result === "delivered" ? "ok" : x.result === "failed" ? "danger" : x.result === "simulated" ? "warn" : "info")} style={{ fontSize: 9.5 }} title={recipientTitle(x)}>
                  {x.result === "delivered" ? "✓" : x.result === "failed" ? "✗" : x.result === "pending" ? "…" : "~"} {x.party === "client" ? "Client" : "Studio"}{x.to ? ` · ${x.to}` : " · no address"}
                </span>
              ))}
              <span style={{ flex: 1 }} />
              <button className="btn ghost sm" style={{ fontSize: 10.5, padding: "3px 9px" }} disabled={busyId === r.id || st.sending} onClick={() => void resend(r.id)}>
                <Icon.send width={11} />{busyId === r.id || st.sending ? "Sending…" : st.tone === "ok" ? "Send again" : "Send now"}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
